import { useQuery } from '@tanstack/react-query';
import { isMonday } from 'date-fns';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import { CookieSetOptions } from 'universal-cookie';

import { useAuth } from '../../../contexts/AuthContext';
import { UserTeam } from '../../../hooks/useUserTeam';
import { TeamIdentity } from '../../../types';
import { SidebarItem } from '../components/SidebarItem';
import { SidebarLogo } from '../components/SidebarLogo';
import { SidebarProfile } from '../components/SidebarProfile';
import { getNavigationItems, TabIds } from '../constants';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { checkReviewerAssignment } from '../queries/checkReviewerAssignment';

function isNotificationEnabled(cookies: Record<string, boolean>): boolean {
  return isMonday(new Date())
    ? cookies?.notifyMonday
    : cookies?.notifyRestOfWeek;
}

function updateNotifyAnalytics(
  cookies: Record<string, boolean>,
  setCookie: (
    name: 'notifyAnalytics',
    value: any,
    options?: CookieSetOptions | undefined,
  ) => void,
): void {
  setCookie('notifyAnalytics', {
    ...cookies,
    [isMonday(new Date()) ? 'notifyMonday' : 'notifyRestOfWeek']: false,
  });
}

export function Sidebar({ teams }: { teams: TeamIdentity[] }): JSX.Element {
  const location = useLocation();
  const { userData } = useAuth();
  const [cookies, setCookie] = useCookies(['notifyAnalytics']);
  const { notifyAnalytics } = useAnalyticsData(
    userData as UserTeam,
    teams,
    cookies,
    setCookie,
  );

  // Check if user is assigned as a reviewer
  const { data: isAssignedAsReviewer } = useQuery({
    queryKey: ['reviewerAssignment', userData?.id],
    queryFn: () => checkReviewerAssignment(userData?.id || ''),
    enabled: !!userData?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
  const navigationItems = getNavigationItems(
    teams,
    userData?.isTeamMember || false,
    isAssignedAsReviewer || false,
    {
      [TabIds.CHECK_IN_USAGE]: {
        notify:
          notifyAnalytics &&
          isNotificationEnabled(
            cookies.notifyAnalytics as Record<string, boolean>,
          ),
        onClick: () => {
          if (
            cookies.notifyAnalytics &&
            isNotificationEnabled(
              cookies.notifyAnalytics as Record<string, boolean>,
            )
          ) {
            updateNotifyAnalytics(
              cookies.notifyAnalytics as Record<string, boolean>,
              setCookie,
            );
          }
        },
      },
    },
  );

  return (
    <div
      className="flex flex-col flex-shrink-0 self-stretch bg-surface-0 border-r border-border-gray min-h-screen
        s:w-[240px] m:w-[260px] l:w-[280px] xl:w-[300px] justify-center"
    >
      <div className="sticky top-0 bg-surface-0 z-50 w-full  p-5">
        <SidebarLogo />
      </div>

      <div className="flex flex-col items-start gap-2 flex-1 bg-surface-0 self-stretch pl-8 overflow-y-auto scrollbar-thin scrollbar-left">
        {navigationItems.map((item) => (
          <div key={item.id} className="w-full">
            <SidebarItem
              item={item}
              isActive={location.pathname.includes(item.path)}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-col sticky bottom-0 bg-surface-0 z-50 w-full  justify-center items-center">
        <SidebarProfile />
      </div>
    </div>
  );
}
